class DotsWidget extends HTMLElement {
  static PANEL_TOP_TRANSITION_MS = 460;

  constructor() {
    super();

    this.stage = this.querySelector('.dots-widget__stage');
    this.field = this.querySelector('.dots-widget__field');
    this.panel = this.querySelector('.dots-widget__panel');
    this.closeButton = this.querySelector('.dots-widget__close');
    this.connector = this.querySelector('.dots-widget__connector');
    this.connectorLine = this.querySelector('.dots-widget__connector-line');
    this.dots = [...this.querySelectorAll('.dots-widget__dot')];
    this.panelItems = [...this.querySelectorAll('.dots-widget__panel-item')];
    this.activeIndex = null;
    this.isAnimating = false;
    this.connectorScheduleTimer = null;
    this.scheduledConnector = null;
    this.hideConnectorTimer = null;
    this.closeTimer = null;
    this.mobileBreakpoint = window.matchMedia('(max-width: 1024px)');

    this.handlePanelTransitionEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'top') return;
      this.completeConnectorSchedule();
    };

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.dotIndex);
        this.activate(index);
      });
    });

    this.closeButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.close();
    });

    this.panelItems.forEach((item) => {
      const nextButton = item.querySelector('.dots-widget__next');
      if (!nextButton) return;

      nextButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const currentIndex = Number(item.dataset.panelIndex);
        const nextIndex = (currentIndex + 1) % this.panelItems.length;
        this.activate(nextIndex);
      });
    });
  }

  connectedCallback() {
    this.onResize = () => {
      if (this.activeIndex === null) return;

      if (this.isMobile()) {
        this.syncStageHeight();
        return;
      }

      this.positionPanel(this.activeIndex, this.isAnimating);
      this.updateConnector(this.activeIndex, false);
    };

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.stage);
    if (this.panel) this.resizeObserver.observe(this.panel);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onResize);
    this.clearConnectorSchedule();
    this.hideConnectorTimer && window.clearTimeout(this.hideConnectorTimer);
    this.closeTimer && window.clearTimeout(this.closeTimer);
  }

  activate(index) {
    if (!this.panelItems[index] || this.isAnimating) return;

    const nextItem = this.panelItems[index];
    const previousIndex = this.activeIndex;

    if (previousIndex === index) return;

    const isSwitch = previousIndex !== null;
    this.hideConnector({ animate: isSwitch ? 'fast' : false });
    this.clearConnectorSchedule();
    this.updateDots(index);

    if (!isSwitch) {
      this.openPanel(nextItem, index);
      this.activeIndex = index;
      return;
    }

    const previousItem = this.panelItems[previousIndex];
    this.switchPanel(previousItem, nextItem, index);
    this.activeIndex = index;
  }

  close() {
    if (this.activeIndex === null) return;

    const activeItem = this.panelItems[this.activeIndex];

    this.dots.forEach((dot) => {
      dot.classList.remove('is-active');
      dot.setAttribute('aria-expanded', 'false');
    });

    activeItem?.classList.remove('is-active', 'is-leaving', 'is-entering');
    activeItem?.setAttribute('aria-hidden', 'true');

    this.panelItems.forEach((item) => {
      item.classList.remove('is-active', 'is-leaving', 'is-entering');
      item.setAttribute('aria-hidden', 'true');
    });

    this.panel.classList.add('is-closing');
    this.panel.classList.remove('is-visible', 'dots-widget__panel--instant', 'is-opening');
    this.panel.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    this.clearConnectorSchedule();
    this.hideConnector({ animate: true });
    this.activeIndex = null;
    this.isAnimating = false;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.panel.classList.remove('is-closing');
      this.panel.style.top = '';
      this.stage.style.minHeight = '';
    }, 400);
  }

  updateDots(index) {
    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  openPanel(item, index) {
    this.panel.classList.remove('is-closing');
    this.panel.classList.add('is-opening', 'is-visible');
    this.panel.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    item.classList.add('is-active');
    item.setAttribute('aria-hidden', 'false');

    window.requestAnimationFrame(() => {
      this.panel.classList.remove('is-opening');

      if (this.isMobile()) {
        this.panel.style.top = '';
        this.syncStageHeight();
        this.scheduleConnectorDraw(index, true, 0);
        return;
      }

      this.positionPanel(index, true);
      this.scheduleConnectorDraw(index, true);
    });
  }

  switchPanel(previousItem, nextItem, index) {
    this.isAnimating = true;

    previousItem.classList.add('is-leaving');
    previousItem.classList.remove('is-active');
    previousItem.setAttribute('aria-hidden', 'true');

    nextItem.classList.add('is-entering');
    nextItem.setAttribute('aria-hidden', 'false');

    window.requestAnimationFrame(() => {
      nextItem.classList.add('is-active');

      if (this.isMobile()) {
        this.panel.style.top = '';
        this.syncStageHeight();
        this.scheduleConnectorDraw(index, true, 0);
      } else {
        this.positionPanel(index, true);
        this.scheduleConnectorDraw(index, true, 360);
      }
    });

    window.setTimeout(() => {
      previousItem.classList.remove('is-leaving');
      nextItem.classList.remove('is-entering');

      if (!this.isMobile()) {
        this.positionPanel(index, true);
      }

      this.syncStageHeight();
      this.isAnimating = false;
    }, 360);
  }

  clearConnectorSchedule() {
    window.clearTimeout(this.connectorScheduleTimer);
    this.panel?.removeEventListener('transitionend', this.handlePanelTransitionEnd);
    this.scheduledConnector = null;
  }

  scheduleConnectorDraw(index, animate = true, delay = DotsWidget.PANEL_TOP_TRANSITION_MS) {
    this.clearConnectorSchedule();
    this.scheduledConnector = { index, animate };

    this.panel.addEventListener('transitionend', this.handlePanelTransitionEnd);
    this.connectorScheduleTimer = window.setTimeout(() => {
      this.completeConnectorSchedule();
    }, delay);
  }

  completeConnectorSchedule() {
    if (!this.scheduledConnector) return;

    const { index, animate } = this.scheduledConnector;
    this.clearConnectorSchedule();
    this.updateConnector(index, animate);
  }

  isMobile() {
    return this.mobileBreakpoint.matches;
  }

  getHeaderOffset() {
    const header = document.querySelector('.section-header');
    return header ? header.getBoundingClientRect().height : 0;
  }

  getDotCenterY(dot) {
    const fieldHeight = this.field?.offsetHeight || this.stage.offsetHeight;
    const dotY = Number.parseFloat(getComputedStyle(dot).getPropertyValue('--dot-y'));
    if (!Number.isNaN(dotY)) {
      return (dotY / 100) * fieldHeight;
    }

    const stageRect = this.stage.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();
    return dotRect.top + dotRect.height / 2 - stageRect.top;
  }

  positionPanel(index, animateTop = true) {
    const dot = this.dots[index];

    if (!dot || !this.panel.classList.contains('is-visible')) return;

    if (this.isMobile()) {
      this.panel.style.top = '';
      return;
    }

    if (!animateTop) {
      this.panel.classList.add('dots-widget__panel--instant');
    }

    const stageRect = this.stage.getBoundingClientRect();
    const stageHeight = this.stage.offsetHeight;
    const panelHeight = this.panel.offsetHeight;
    const margin = 16;
    const headerOffset = this.getHeaderOffset();

    const viewportTop = headerOffset + margin;
    const viewportBottom = window.innerHeight - margin;
    const stageViewportTop = viewportTop - stageRect.top;
    const stageViewportBottom = viewportBottom - stageRect.top;

    const dotCenterY = this.getDotCenterY(dot);
    let panelTop = dotCenterY - panelHeight / 2;

    if (panelTop < stageViewportTop) {
      panelTop = stageViewportTop;
    }

    if (panelTop + panelHeight > stageViewportBottom) {
      panelTop = stageViewportBottom - panelHeight;
    }

    const maxTop = Math.max(margin, stageHeight - panelHeight - margin);
    panelTop = Math.max(margin, Math.min(panelTop, maxTop));

    this.panel.style.top = `${panelTop}px`;

    if (!animateTop) {
      void this.panel.offsetHeight;
      this.panel.classList.remove('dots-widget__panel--instant');
    }
  }

  syncStageHeight() {
    if (!this.mobileBreakpoint.matches || !this.panel.classList.contains('is-visible')) {
      this.stage.style.minHeight = '';
      return;
    }

    const stageRect = this.stage.getBoundingClientRect();
    const panelRect = this.panel.getBoundingClientRect();
    const image = this.querySelector('.dots-widget__image');
    const imageRect = image?.getBoundingClientRect();
    const imageBottom = imageRect ? imageRect.bottom - stageRect.top : 0;
    const panelBottom = panelRect.bottom - stageRect.top;
    const minHeight = Math.max(0, Math.ceil(Math.max(imageBottom, panelBottom) - 16));

    this.stage.style.minHeight = `${minHeight}px`;
  }

  updateConnector(index, animate = true) {
    const dot = this.dots[index];

    window.clearTimeout(this.hideConnectorTimer);
    this.hideConnectorTimer = null;

    if (!dot || !this.panel.classList.contains('is-visible')) {
      this.hideConnector();
      return;
    }

    const stageRect = this.stage.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();
    const panelRect = this.panel.getBoundingClientRect();

    if (!stageRect.width || !stageRect.height) return;

    const x1 = dotRect.left + dotRect.width / 2 - stageRect.left;
    const y1 = dotRect.top + dotRect.height / 2 - stageRect.top;
    const x2 = panelRect.left + panelRect.width / 2 - stageRect.left;
    const y2 = panelRect.top + panelRect.height / 2 - stageRect.top;

    this.connector.setAttribute('width', stageRect.width);
    this.connector.setAttribute('height', stageRect.height);
    this.connector.setAttribute('viewBox', `0 0 ${stageRect.width} ${stageRect.height}`);

    this.connectorLine.setAttribute('x1', x1);
    this.connectorLine.setAttribute('y1', y1);
    this.connectorLine.setAttribute('x2', x2);
    this.connectorLine.setAttribute('y2', y2);

    const length = this.connectorLine.getTotalLength();
    const dashPattern = '2 10';
    this.connector.classList.add('is-visible');

    if (animate && length > 0) {
      this.connectorLine.style.transition = 'none';
      this.connectorLine.style.strokeDasharray = dashPattern;
      this.connectorLine.style.strokeDashoffset = `${length}`;
      void this.connectorLine.getBoundingClientRect();
      this.connectorLine.style.transition = 'stroke-dashoffset 0.55s ease-out';
      this.connectorLine.style.strokeDashoffset = '0';
      return;
    }

    this.connectorLine.style.transition = 'none';
    this.connectorLine.style.strokeDasharray = dashPattern;
    this.connectorLine.style.strokeDashoffset = '0';
  }

  hideConnector({ animate = false } = {}) {
    if (!this.connector || !this.connectorLine) return;

    window.clearTimeout(this.hideConnectorTimer);
    this.connectorLine.style.transition = 'none';

    if (animate === true) {
      this.connector.style.transition = 'opacity 0.4s ease, visibility 0.4s ease';
      this.connector.classList.remove('is-visible');
      this.hideConnectorTimer = window.setTimeout(() => {
        this.resetConnectorStyles();
      }, 400);
      return;
    }

    if (animate === 'fast') {
      this.connector.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';
      this.connector.classList.remove('is-visible');
      this.hideConnectorTimer = window.setTimeout(() => {
        this.resetConnectorStyles();
      }, 300);
      return;
    }

    this.connector.style.transition = 'none';
    this.connector.classList.remove('is-visible');

    this.hideConnectorTimer = window.setTimeout(() => {
      if (!this.connector.classList.contains('is-visible')) {
        this.resetConnectorStyles();
      }
    }, 0);
  }

  resetConnectorStyles() {
    if (!this.connector || !this.connectorLine) return;

    this.connector.style.transition = '';
    this.connectorLine.style.strokeDasharray = '';
    this.connectorLine.style.strokeDashoffset = '';
  }
}

customElements.define('dots-widget', DotsWidget);
